import React from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import Loader from 'react-loader-spinner';
import styles from './styles.css';
import buttonStyles from '../Common/button.css';
import formStyles from '../Common/form.css';
import { useI18n, Link, getLanguageFromURL } from '../../utils/i18n';
import { SELL_WITH_AGENT } from '../../configs/urls';

import LogoWithTextEn from '../../public/logo/SVG/logo_text_en.svg';
import LogoWithTextAr from '../../public/logo/SVG/logo_text_ar.svg';

export const TranslationsNeeded = 'components/SellAgentPage/translations';

type FormData = {
  name: string;
  city: string;
  fullAddress: string;
  phoneNumber: string;
  email: string;
  propertyType: string;
};

const onSubmit = async (
  data: FormData,
  setIsSubmitting: (val: boolean) => void,
  router: ReturnType<typeof useRouter>,
): Promise<void> => {
  setIsSubmitting(true);
  try {
    const res = await fetch(SELL_WITH_AGENT, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...data,
      }),
    });

    if (res.status === 201) {
      const currentLang = getLanguageFromURL();
      router.push(`/${currentLang}/sell/agent/success`);
    }
  } catch (e) {
    alert('Sorry, something went wrong.\nWe\'re working on a fix now.');
  } finally {
    setIsSubmitting(false);
  }
};

const Component: React.FC = () => {
  const { translations, language } = useI18n(TranslationsNeeded);
  const { handleSubmit, register, reset } = useForm<FormData>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const router = useRouter();

  return (
    <section className={styles.section}>
      <div className={styles.sectionContent}>
        <Link href="">
          <a className={styles.logoWrapper}>
            {
              language === 'en' ? <LogoWithTextEn /> : <LogoWithTextAr />
            }
          </a>
        </Link>

        <main className={styles.allSections}>
          <div className={styles.infoSection}>
            <h1 className={styles.title}>
              {translations.title}
            </h1>

            <p className={styles.description}>
              {translations.description}
            </p>

            <div className={styles.benefits}>
              {
                translations.benefits.map((benefit: {title: string, description: string}) => (
                  <div className={styles.benefit} key={benefit.title}>
                    <h3 className={styles.h3}>
                      {benefit.title}
                    </h3>

                    <p className={styles.text}>
                      {benefit.description}
                    </p>
                  </div>
                ))
              }
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.card}>

              <form
                onSubmit={handleSubmit(
                  async (data: FormData) => {
                    await onSubmit(data, setIsSubmitting, router);
                    // reset();
                  },
                )}
                className={styles.form}
              >
                <h2 className={styles.h2}>
                  {translations.formTitle}
                </h2>

                <p className={styles.text}>
                  {translations.formDescription}
                </p>

                <div className={styles.inputWrapper}>
                  <div className={styles.inputPart}>
                    <label htmlFor="name" className={styles.label}>
                      {translations.nameLabel}
                    </label>

                    <input
                      name="name"
                      id="name"
                      type="text"
                      required
                      placeholder={translations.namePlaceholder as string}
                      className={formStyles.input}
                      ref={register}
                    />
                  </div>

                  <div className={styles.inputPart}>
                    <label htmlFor="city" className={styles.label}>
                      {translations.cityLabel}
                    </label>

                    <input
                      name="city"
                      id="city"
                      type="text"
                      required
                      placeholder={translations.cityPlaceholder as string}
                      className={formStyles.input}
                      ref={register}
                    />
                  </div>

                  <div className={styles.inputPart}>
                    <label htmlFor="fullAddress" className={styles.label}>
                      {translations.fullAddressLabel}
                    </label>

                    <input
                      name="fullAddress"
                      id="fullAddress"
                      type="text"
                      required
                      placeholder={translations.fullAddressPlaceholder as string}
                      className={formStyles.input}
                      ref={register}
                    />
                  </div>

                  <div className={styles.inputPart}>
                    <label htmlFor="propertyType" className={styles.label}>
                      {translations.propertyTypeLabel}
                    </label>
                    <select
                      name="propertyType"
                      placeholder={translations.propertyTypePlaceholder as string}
                      required
                      id="propertyType"
                      className={[
                        formStyles.input,
                        formStyles.select,
                      ].join(' ')}
                      ref={register}
                      // onChange={(e) => {
                      //   setCurrentSize(e.target.value);
                      // }}
                      onBlur={() => {}}
                    >
                      {
                        translations.propertyTypesAvailable.map((propertyType: string) => (
                          <option
                            key={propertyType}
                            value={propertyType}
                          >
                            {propertyType}
                          </option>
                        ))
                      }
                    </select>

                  </div>

                  <div className={styles.inputPart}>
                    <label htmlFor="phoneNumber" className={styles.label}>
                      {translations.phoneLabel}
                    </label>

                    <input
                      name="phoneNumber"
                      id="phoneNumber"
                      type="text"
                      required
                      placeholder={translations.phonePlaceholder as string}
                      className={formStyles.input}
                      ref={register}
                    />
                  </div>

                  <div className={styles.inputPart}>
                    <label htmlFor="email" className={styles.label}>
                      {translations.emailLabel}
                    </label>

                    <input
                      name="email"
                      id="email"
                      type="email"
                      required
                      placeholder={translations.emailPlaceholder as string}
                      className={formStyles.input}
                      ref={register}
                    />
                  </div>

                </div>
                <div className={styles.buttonWrapper}>
                  <button
                    type="submit"
                    className={[
                      buttonStyles.button,
                      buttonStyles.primary,
                      buttonStyles.large,
                    ].join(' ')}
                    disabled={isSubmitting}
                  >
                    {
                      isSubmitting ? (
                        <div className={styles.buttonDisabled}>
                          <p>
                            {translations.submitting}
                          </p>
                          <Loader
                            type="TailSpin"
                            color="#f4f4f4"
                            height={30}
                            width={30}
                            timeout={1000000}
                          />
                        </div>
                      ) : (
                        translations.submitRequest
                      )
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </section>
  );
};

export const AllTranslationsNeeded: string[] = [
  TranslationsNeeded,
];

export default Component;
